// In-car HUD: speedometer, the highway stretch + trip distance, the radio, and your passengers
// (with their chatter). Shown whenever you're driving or riding.
import { signal } from "@preact/signals";
import { useEffect, useState } from "preact/hooks";
import { phoneOpen, playerLoc, world } from "../bridge";
import { radio, STATIONS, toggleRadio, tuneRadio } from "../radio";

/** Bumped by main when you hit traffic, so passengers react. */
export const carBump = signal(0);

const pick = <T,>(a: T[]) => a[Math.floor(Math.random() * a.length)];

function chatter(kmh: number, highway: string | null, van: boolean, bumped: boolean): string {
  if (bumped) return pick(["😱 Abeg drive well!", "my coffee 😭", "insurance na scam anyway 💀", "WATCH THE ROAD SER"]);
  if (kmh > 150) return pick(["😱 slow down abeg!", "this is faster than my portfolio dumping 🎢", "we're going to the moon 🚀", "I can see my life flashing"]);
  const r = radio.value;
  if (r.on && Math.random() < 0.35) return pick([`🔥 ${r.title} is hard`, `turn it UP, ${STATIONS[r.station].name} never misses`, "this song is my whole personality", "who made this beat??"]);
  if (!r.on && Math.random() < 0.25) return pick(["no music? 😴", "put on Amapiano Wave abeg", "radio please 🎶"]);
  if (van && Math.random() < 0.3) return pick(["Oshodi! Oshodi! 🚌", "Owa! Driver, I dey drop here o", "change dey? 🪙"]);
  if (highway && Math.random() < 0.4) return pick(["road trip szn 🛣️", "where are we even going 😂", "this view though 😍", `${highway} is beautiful`, "wake me up when we're there"]);
  return pick(["gm driver ☀️", "nice whip ser", "you see the charts today?", "I'm up 3x this week, I'll tip you 😏", "wagmi", "this is so much better than the Danfo"]);
}

export function CarHud() {
  const loc = playerLoc.value;
  const [line, setLine] = useState<{ who: string; text: string } | null>(null);
  const bump = carBump.value;
  // Passengers talk every ~8-12 s, and right away when you bump something.
  useEffect(() => {
    if (!loc.driving || !loc.riders.length) {
      setLine(null);
      return;
    }
    const say = (bumped: boolean) => {
      const l = playerLoc.value;
      if (!l.riders.length) return;
      setLine({ who: pick(l.riders).handle, text: chatter(l.kmh, l.highway?.name ?? null, l.seats >= 6, bumped) });
    };
    if (bump) say(true);
    const id = setInterval(() => say(false), 8000 + Math.random() * 4000);
    return () => clearInterval(id);
  }, [loc.driving, loc.riders.length, bump]);

  if (!loc.driving || phoneOpen.value) return null;
  const r = radio.value;
  const st = STATIONS[r.station];
  return (
    <div class="car-hud" role="region" aria-label="Car">
      <div class="car-row">
        <div class="speedo">
          <b>{loc.kmh}</b>
          <span>km/h</span>
        </div>
        {loc.highway ? (
          <div class="trip">
            <div class="trip-road">🛣️ {loc.highway.name}</div>
            <div class="muted small">{loc.highway.km.toFixed(1)} km this trip</div>
          </div>
        ) : (
          <button class="btn small hw-btn" onClick={() => world()?.enterHighway()} title="Hit the highway (H)">
            🛣️ Road trip <span class="kbd">H</span>
          </button>
        )}
      </div>
      <div class={`radio ${r.on ? "on" : ""}`} style={{ "--st": st.color }}>
        <button class="radio-btn" aria-label={r.on ? "Radio off" : "Radio on"} title="Radio (R)" onClick={() => toggleRadio()}>
          {r.on ? "📻" : "⏻"}
        </button>
        <button class="radio-btn" aria-label="Previous station" onClick={() => tuneRadio(-1)}>
          ◀
        </button>
        <div class="radio-screen" aria-live="polite">
          {r.on ? (
            <>
              <div class="radio-st">
                {st.name} <span>{st.freq}</span>
              </div>
              <div class="radio-song">
                ♪ {r.title} · {r.artist}
              </div>
            </>
          ) : (
            <div class="radio-st muted">Radio off · tap ⏻</div>
          )}
        </div>
        <button class="radio-btn" aria-label="Next station" title="Next station (T)" onClick={() => tuneRadio(1)}>
          ▶
        </button>
      </div>
      {r.on && r.dj && <div class="radio-dj">🎙️ {r.dj}</div>}
      {loc.seats > 0 && (
        <div class={`riders ${loc.riders.length > 2 ? "many" : ""}`}>
          <span class="muted small">
            👥 {loc.riders.length}/{loc.seats}
          </span>
          {loc.riders.map((p) => (
            <button key={p.id} class="rider-chip" title="Drop off" onClick={() => world()?.dropOff(p.id)}>
              @{p.handle} ✕
            </button>
          ))}
          {loc.riders.length > 1 && (
            <button class="link small" onClick={() => world()?.dropOff()}>
              Drop everyone
            </button>
          )}
          {!loc.riders.length && !loc.highway && <span class="muted small">Tap someone on the street to give them a ride 🚕</span>}
        </div>
      )}
      {line && (
        <div class="rider-say" key={line.text}>
          💬 <b>@{line.who}</b> {line.text}
        </div>
      )}
    </div>
  );
}
